import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { routePattern } from '@sh/shared'
import type { ClientEvent } from '@sh/shared'

declare global {
  interface Window {
    /**
     * Present only when the deployment injected the tracker tag, and absent on
     * every LAN game, every dev server and every deployment that left analytics
     * off. Optional all the way down for that reason.
     */
    umami?: {
      track?: (build: (payload: Record<string, unknown>) => Record<string, unknown>) => void
    }
  }
}

/**
 * Reports which screen is on, and nothing else.
 *
 * The tracker is loaded with `data-auto-track="false"`, so it never reads
 * `location.pathname` itself - this is the only thing that tells it anything.
 * That is deliberate: the same rule the server follows for game state applies
 * here, that the way to keep a secret out of somewhere is never to send it,
 * not to send it and filter afterwards. `/play/QK7M` becomes `/play/:code`
 * before it leaves the phone.
 *
 * `referrer` is blanked explicitly. On a full page load of `/play/QK7M` the
 * document referrer is `/join/QK7M` - the lobby code arriving by the back door,
 * in a field nobody thinks to look at.
 *
 * The callback form is load-bearing rather than a style choice. Handed a plain
 * object the tracker sends that object and nothing else - no website id, no
 * hostname, no screen, no language - because it replaces its own payload rather
 * than merging into it. Handed a function it passes that payload in, so this
 * overwrites the two fields that must not go out as the tracker computed them
 * and leaves the rest of what Umami wants intact.
 *
 * In development React's StrictMode runs this effect twice, so a dev server
 * with analytics configured double-counts. Nobody configures analytics in
 * development, and it is not worth a guard that would also fire in production.
 */
export function Analytics(): null {
  const { pathname } = useLocation()

  useEffect(() => {
    try {
      window.umami?.track?.((payload) => ({
        ...payload,
        url: routePattern(pathname),
        referrer: '',
        title: document.title,
      }))
    } catch {
      // A blocked script, a dead analytics host, a browser that refused the
      // request. None of it is anybody's business at the table.
    }
  }, [pathname])

  return null
}

/**
 * Count one of the fixed funnel events. A name and nothing else - the type
 * allows no data, and the server and the page guard would drop it if it did.
 *
 * Sent the same way a pageview is, through the callback, so it carries the
 * route pattern rather than whatever is in the address bar.
 */
export function trackEvent(name: ClientEvent): void {
  try {
    window.umami?.track?.((payload) => ({
      ...payload,
      name,
      url: routePattern(window.location.pathname),
      referrer: '',
      title: document.title,
    }))
  } catch {
    // Same as a pageview: nothing about analytics is allowed to break a page.
  }
}
